import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Shield, 
  Building2, 
  User, 
  Loader2, 
  ExternalLink,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

const TEST_ACCOUNTS = [
  {
    id: 'super-admin',
    title: 'Fundle Brain Super Admin',
    description: 'Access ALL brands, manage users, platform settings',
    email: 'admin@fundle.ai',
    password: 'fundle-super-admin-2026',
    role: 'super_admin',
    icon: Shield,
    color: 'from-yellow-500 to-orange-500',
    features: [
      'View all brands in dropdown',
      'Brand Management',
      'Platform Settings',
      'User Management for all brands'
    ]
  },
  {
    id: 'newu-admin',
    title: 'NewU Brand Admin',
    description: 'Full access to NewU brand only',
    email: 'admin@newu.in',
    password: 'newu-admin-2026',
    role: 'brand_admin',
    icon: Building2,
    color: 'from-blue-500 to-cyan-500',
    features: [
      'NewU dashboards only',
      'User Management (NewU)',
      'Cannot access other brands',
      'No Platform Settings'
    ]
  },
  {
    id: 'newu-user',
    title: 'NewU Brand User',
    description: 'Limited access based on permissions',
    email: 'user@newu.in',
    password: 'newu-user-2026',
    role: 'brand_user',
    icon: User,
    color: 'from-green-500 to-emerald-500',
    features: [
      'Only assigned sections visible',
      'No User Management',
      'No Brand Management',
      'Read-only analytics'
    ]
  }
];

export default function TestLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loggingIn, setLoggingIn] = useState(null);

  const handleLogin = async (account) => {
    setLoggingIn(account.id);
    
    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: account.email,
          password: account.password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        login(data.user);
        toast.success(`Logged in as ${account.title}`);
        navigate('/dashboard');
      } else {
        toast.error(data.detail || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed');
    } finally {
      setLoggingIn(null);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6" data-testid="test-login-page">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold mb-3">
            🧪 Test Environment
          </h1>
          <p className="text-muted-foreground text-lg">
            Choose a test account to explore different user roles and permissions
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-sm">
            <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
            Preview Mode - Simulating multi-tenant subdomains
          </div>
        </div>

        {/* Test Accounts Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {TEST_ACCOUNTS.map((account) => {
            const Icon = account.icon;
            const isLoading = loggingIn === account.id;
            
            return (
              <div
                key={account.id}
                className="rounded-2xl border border-border bg-card overflow-hidden hover:shadow-xl transition-all duration-300"
                data-testid={`test-account-${account.id}`}
              >
                {/* Header with gradient */}
                <div className={`p-6 bg-gradient-to-r ${account.color}`}>
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{account.title}</h3>
                      <p className="text-white/80 text-sm">{account.role.replace('_', ' ')}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  <p className="text-muted-foreground text-sm">
                    {account.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      What you'll see:
                    </p>
                    <ul className="space-y-1.5">
                      {account.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Credentials */}
                  <div className="p-3 rounded-lg bg-muted/50 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <code className="text-xs bg-background px-2 py-0.5 rounded">{account.email}</code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Password:</span>
                      <code className="text-xs bg-background px-2 py-0.5 rounded">{account.password}</code>
                    </div>
                  </div>

                  {/* Login Button */}
                  <Button
                    className="w-full"
                    onClick={() => handleLogin(account)}
                    disabled={loggingIn !== null}
                    data-testid={`login-${account.id}`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Logging in...
                      </>
                    ) : (
                      <>
                        Login as {account.role.replace('_', ' ')}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-10 text-center space-y-4">
          <div className="inline-flex items-center gap-4 text-sm text-muted-foreground">
            <a href="/signin" className="hover:text-foreground transition-colors flex items-center gap-1">
              <ExternalLink className="w-4 h-4" />
              Manual Login
            </a>
            <span>•</span>
            <a href="/" className="hover:text-foreground transition-colors">
              Back to Home
            </a>
          </div>
          
          <p className="text-xs text-muted-foreground">
            In production, each brand will have its own subdomain (e.g., newuAI.fundledata.com)
          </p>
        </div>
      </div>
    </div>
  );
}
